import React, { useMemo, useState } from 'react';

import { Menu, notification, Popover } from 'antd';
import Axios from 'axios';
import { difference } from 'lodash';

import styled, { keyframes } from 'styled-components';

import { useUserPermission } from '@contexts/UserContext';

import { getContactName, getModel, usePlaylistsCount } from '@hooks/API/Contact/UseContactsList';
import { useModelBooksPreview } from '@hooks/API/Contact/UseModelBooksPreview';
import { forceReloadActivePackages, useActivePackages } from '@hooks/API/Packages/UsePackages';

import AddModelBooksPreviewPopover from '@components/ActivePackages/AddModelBooksPreviewPopover';
import AddPackageDrawer from '@components/ActivePackages/AddPackageDrawer';
import { ModelBookPreviewCover, PREVIEW_COVER_SIZE } from '@components/ActivePackages/ModelBooksPreviewRows';
import NwIcon from '@components/Gui/NwIcon';
import PackageDrawer from '@components/PackageDrawer/PackageDrawer';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/**
 * Il menu si apre da solo all'hover, quindi la soglia e' piu' alta di quella
 * dell'anteprima: comparire mentre si attraversa il wall darebbe fastidio.
 */
const MENU_HOVER_DELAY = 0.3;
const HOVER_AND_CLICK = ['hover', 'click'];

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: .45; }
`;

const PopoverContent = styled.div`
    padding-bottom: 1rem;
    min-width: 20rem;

    /* L'altezza e' riservata da subito con gli scheletri: quando le copertine arrivano il
       menu non salta sotto al cursore. Il numero di scheletri e' quello dei book. */
    .preview-strip {
        display: flex;
        gap: 0.35rem;
        padding: 0.6rem 1rem;
        overflow-x: auto;
        border-bottom: ${(props) => props.theme.DefaultBorder};
    }

    .preview-strip-skeleton {
        width: ${PREVIEW_COVER_SIZE}px;
        height: ${PREVIEW_COVER_SIZE}px;
        flex: none;
        background: ${(props) => props.theme.BorderColor};
        animation: ${pulse} 1300ms ease-in-out infinite;
    }

    @media (prefers-reduced-motion: reduce) {
        .preview-strip-skeleton {
            animation: none;
        }
    }

    /* Dice una volta sola quali e quanti book si stanno muovendo: le voci del menu
       sotto restano generiche e parlano solo della destinazione. */
    .popover-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.6rem 1rem;
        border-bottom: 2px solid ${(props) => props.theme.PrimaryColor};
        text-transform: uppercase;
        font-size: 0.8rem;
        font-weight: 600;

        svg {
            font-size: 1rem;
            color: ${(props) => props.theme.PrimaryColor};
        }
    }

    .error-message {
        padding: 1rem 2rem;
        text-align: center;
        color: ${(props) => props.theme.DangerColor};
        text-transform: uppercase;
        max-width: 20rem;
    }

    .ant-menu-vertical {
        border-right: none;
    }

    .ant-menu {
        border-radius: 0 0 6px 6px;
    }
    .ant-menu-item {
        margin: 0 !important;
        border-bottom: ${(props) => props.theme.DefaultBorder};
        height: 2rem;
        line-height: 2rem;

        &:last-child {
            border-bottom: none;
        }

        text-transform: uppercase;
        font-size: 0.8rem;
        vertical-align: middle;

        svg {
            margin: 0 6px;
        }

        .active {
            font-weight: bold;

            svg {
                color: ${(props) => props.theme.DangerColor};
            }
        }

        .badge {
            color: #fff;
            background-color: ${(props) => props.theme.DangerColor};
            display: inline-block;
            padding: 0.25em 0.4em;
            font-size: 0.75rem;
            font-weight: 700;
            line-height: 1;
            text-align: center;
            white-space: nowrap;
            margin-top: 0.2em;
            vertical-align: baseline;
            border-radius: 0.25rem;
            transition:
                color 0.15s ease-in-out,
                background-color 0.15s ease-in-out,
                border-color 0.15s ease-in-out,
                box-shadow 0.15s ease-in-out;
        }
    }
`;

/** Il fast package ha un endpoint dedicato che risolve i book lato server. */
const addFastPackageBooks = async (packageID, modelID, books, packageGroup) => {
    const data = {
        PackageID: packageID,
        Models: [modelID],
        PackageGroup: packageGroup,
    };
    await Axios.post('/PackagePlaylists/AddFastPackages', data);
};

/**
 * Senza un endpoint dedicato come quello del fast package: una POST per book, in sequenza
 * per non perdere l'ordine. Un book già nel package non è un errore, si salta e si prosegue.
 */
const addBooksOneByOne = async (packageID, modelID, books, packageGroup) => {
    for (const playlistID of books) {
        const data = {
            packageID: packageID,
            playlistID: playlistID,
            PackageGroup: packageGroup,
        };
        try {
            await Axios.post('/PackagePlaylists', data);
        } catch (error) {
            if (error.errorCode !== 'EntityAlreadyExists') {
                throw error;
            }
        }
    }
};

/**
 * Fast package, comp cards e favourites si comportano allo stesso modo: cambiano solo
 * quali book della modella usano (FP, CP o FAV, dalla cache playlistsCount), l'icona,
 * i testi e il modo di aggiungerli a un package. booksLabel e' il pezzo che cambia nelle
 * frasi che dicono quanti book stanno per entrare nel package.
 */
const VARIANTS = {
    fastPackage: {
        icon: byPrefixAndName.fasl['truck-fast'],
        getBooks: (modelPlaylistsCount) => modelPlaylistsCount.FP,
        addBooksToPackage: addFastPackageBooks,
        noBooksTooltip: 'No fast package books set for this model',
        alreadyAddedTooltip: 'Fast package already in active package',
        booksLabel: (count) => `${count} fast package book${count > 1 ? 's' : ''}`,
        errorMessage: 'Error adding fast package',
    },
    compCards: {
        icon: byPrefixAndName.fasl['image-polaroid-user'],
        getBooks: (modelPlaylistsCount) => modelPlaylistsCount.CP,
        addBooksToPackage: addBooksOneByOne,
        noBooksTooltip: 'No comp cards set for this model',
        alreadyAddedTooltip: 'Comp cards already in active package',
        booksLabel: (count) => `${count} comp card${count > 1 ? 's' : ''}`,
        errorMessage: 'Error adding comp cards',
    },
    favourites: {
        icon: byPrefixAndName.fasl['heart'],
        getBooks: (modelPlaylistsCount) => modelPlaylistsCount.FAV,
        addBooksToPackage: addBooksOneByOne,
        noBooksTooltip: 'No favourite books for this model',
        alreadyAddedTooltip: 'Favourite books already in active package',
        booksLabel: (count) => `${count} favourite book${count > 1 ? 's' : ''}`,
        errorMessage: 'Error adding favourite books',
    },
};

const findModelInPackage = (pack, modelID) =>
    pack && pack.Models ? pack.Models.find((model) => model.ModelID === Number(modelID)) : null;

/** I gruppi del package sono i Group distinti e non vuoti delle sue modelle, come groupPackage. */
const getPackageGroupNames = (pack) => {
    if (!pack || !pack.Models) {
        return [];
    }
    return [...new Set(pack.Models.map((model) => model.Group).filter((group) => group))];
};

/**
 * Dove finiscono i book in questo package. Una modella sta in un solo gruppo: se è già
 * nel package il gruppo è deciso e chiederlo sarebbe sbagliato, e senza gruppi non c'è
 * niente da scegliere. Resta un caso solo: modella fuori e package con gruppi.
 */
const getGroupDestination = (pack, modelID) => {
    const modelInPackage = findModelInPackage(pack, modelID);
    if (modelInPackage) {
        return { locked: true, group: modelInPackage.Group || '' };
    }
    const groupNames = getPackageGroupNames(pack);
    if (groupNames.length === 0) {
        return { locked: true, group: '' };
    }
    return { locked: false, groupNames };
};

/** Un gruppo per voce più "no group": sono destinazioni diverse, non un default. */
const buildGroupActions = (pack, groupNames) => [
    ...groupNames.map((group, index) => ({
        name: group,
        key: `add-${pack.ID}-g${index}`,
        icon: byPrefixAndName.fasl['people-group'],
    })),
    {
        name: 'no group',
        key: `add-${pack.ID}-none`,
        icon: byPrefixAndName.fasl['plus-square'],
    },
];

const AddModelBooksToPackageButton = ({ modelID, noTooltip, variant }) => {
    const config = VARIANTS[variant];
    const [isAdding, setIsAdding] = useState(false);
    const [createNewPackage, setCreateNewPackage] = useState(false);
    const [newPackageId, setNewPackageId] = useState();
    const [isSingleSend, setIsSingleSend] = useState(false);
    const model = getModel(Number(modelID));
    const viewModelBooks = useUserPermission('ViewModelBooks', model && model.U && model.U.map((user) => user.UserID));
    const { data: packages } = useActivePackages();
    const { data: playlistsCount } = usePlaylistsCount();
    const [open, setOpen] = useState(false);
    const { books: previewBooks, isLoading: previewLoading } = useModelBooksPreview(variant, modelID, open);
    // Su mobile l'hover non esiste e un trigger hover si aprirebbe al tap: solo click.
    const menuTrigger = noTooltip ? 'click' : HOVER_AND_CLICK;

    const modelBooks = useMemo(() => {
        if (playlistsCount && playlistsCount.length > 0) {
            const modelInPlaylistsCount = playlistsCount.find((count) => count.ID === modelID);
            if (modelInPlaylistsCount) {
                const books = config.getBooks(modelInPlaylistsCount);
                if (books && books.length > 0) {
                    return books;
                }
            }
        }
        return null;
    }, [playlistsCount, modelID, config]);

    const singleActivePackage = useMemo(() => {
        if (packages && packages.length === 1) {
            return packages[0];
        }
        return null;
    }, [packages]);

    const booksInPackage = (packageId) => {
        const pack = packages.find((p) => p.ID === packageId);
        if (pack && pack.Models && pack.Models.length > 0) {
            const model = pack.Models.find((model) => model.ModelID === Number(modelID));
            if (model && model.Playlists && model.Playlists.length > 0) {
                const allModelBooksInModelPlaylists = modelBooks.every((book) =>
                    model.Playlists.some((playlist) => playlist.PlaylistID === book)
                );
                return allModelBooksInModelPlaylists;
            }
        }
        return false;
    };

    /**
     * Il token nella chiave dice il gruppo: 'keep' segue la modella dov'è già, 'none'
     * la mette fuori dai gruppi, 'gN' è l'ennesimo gruppo del package.
     */
    const resolveGroup = (packageId, groupToken) => {
        const pack = packages ? packages.find((p) => p.ID === packageId) : null;
        if (groupToken === 'keep') {
            const modelInPackage = findModelInPackage(pack, modelID);
            return modelInPackage && modelInPackage.Group ? modelInPackage.Group : '';
        }
        if (groupToken && groupToken.startsWith('g')) {
            return getPackageGroupNames(pack)[Number(groupToken.slice(1))] || '';
        }
        return '';
    };

    /** Una voce sola, oppure il package con i suoi gruppi in un submenu. */
    const buildAddAction = (pack) => {
        const destination = getGroupDestination(pack, modelID);
        if (destination.locked) {
            return {
                name: destination.group ? `${pack.Name} · ${destination.group}` : pack.Name,
                key: `add-${pack.ID}-keep`,
                icon: byPrefixAndName.fasl['plus-square'],
            };
        }
        return {
            name: pack.Name,
            key: `addgroup-${pack.ID}`,
            icon: byPrefixAndName.fasl['plus-square'],
            children: buildGroupActions(pack, destination.groupNames),
        };
    };

    const drodownActions = useMemo(() => {
        const actions = [];
        if (modelBooks) {
            const newpackageaction = {
                name: 'create a new package',
                key: 'create-0',
                icon: byPrefixAndName.fasl['book-arrow-right'],
                added: false,
            };
            if (packages && packages.length > 0) {
                const packages_enriched = packages.map((p) => ({
                    ...p,
                    isAdded: booksInPackage(p.ID),
                }));
                const packagesWith = packages_enriched.filter((p) => {
                    return p.isAdded;
                });
                const packagesWithout = difference(packages_enriched, packagesWith);
                // Un package solo, con gruppi e senza la modella dentro: la scelta sono i
                // gruppi, e un livello per il package sarebbe un hover a vuoto.
                const onlyPackage = packages.length === 1 && packagesWithout.length === 1 ? packagesWithout[0] : null;
                const onlyPackageDestination = onlyPackage ? getGroupDestination(onlyPackage, modelID) : null;
                if (onlyPackageDestination && !onlyPackageDestination.locked) {
                    actions.push({
                        group: true,
                        name: `add to "${onlyPackage.Name}"`,
                        subactions: [
                            ...buildGroupActions(onlyPackage, onlyPackageDestination.groupNames),
                            newpackageaction,
                        ],
                    });
                } else if (packagesWithout.length > 0) {
                    actions.push({
                        group: true,
                        name: 'add to a package',
                        subactions: [newpackageaction, ...packagesWithout.map(buildAddAction)],
                    });
                } else {
                    actions.push({
                        group: true,
                        name: 'add to a package',
                        subactions: [newpackageaction],
                    });
                }
                if (packagesWith.length) {
                    const removesubactions = packagesWith.map((p) => {
                        const modelInPackage = findModelInPackage(p, modelID);
                        const group = modelInPackage ? modelInPackage.Group : null;
                        return {
                            name: group ? `${p.Name} · ${group}` : p.Name,
                            key: `remove-${p.ID}`,
                            icon: byPrefixAndName.fasl['minus-square'],
                        };
                    });
                    actions.push({
                        group: true,
                        name: 'remove from package',
                        subactions: [...removesubactions],
                    });
                }
            } else {
                actions.push({
                    group: true,
                    name: 'add to a package',
                    subactions: [newpackageaction],
                });
            }
            actions.push({
                group: true,
                name: 'quick send',
                subactions: [
                    {
                        name: 'send as a single model package',
                        key: 'send-0',
                        icon: byPrefixAndName.fasl['book-circle-arrow-right'],
                        added: false,
                    },
                ],
            });
        }
        return actions;
    }, [packages, modelBooks, modelID]);

    const renderAction = (action, index) => {
        if (action.group) {
            return {
                type: 'group',
                key: ` group-${index}`,
                label: action.name,
                children: action.subactions.map(renderAction),
            };
        }
        const item = {
            key: action.key,
            label: (
                <span>
                    <NwIcon icon={action.icon} />
                    {action.name}
                </span>
            ),
        };
        // Le voci con figli sono i package con gruppi: la destinazione si sceglie nel submenu.
        if (action.children) {
            item.children = action.children.map(renderAction);
        }
        return item;
    };

    const onClick = (e) => {
        const [action, packageIdSegment, groupToken] = e.key.split('-');
        const clickedPackageId = Number(packageIdSegment);
        switch (action) {
            case 'create':
                setCreateNewPackage(true);
                break;
            case 'add':
                addBooksToPackage(clickedPackageId, resolveGroup(clickedPackageId, groupToken));
                break;
            case 'remove':
                removeBooksFromPackage(clickedPackageId);
                break;
            case 'send':
                sendAsASinglePackage();
                break;
            default:
                break;
        }
        setOpen(false);
    };

    const booksInSingleActivePackage = useMemo(() => {
        if (packages && packages.length === 1 && modelBooks) {
            if (packages[0].Models && packages[0].Models.length > 0) {
                const model = packages[0].Models.find((model) => model.ModelID === Number(modelID));
                if (model && model.Playlists && model.Playlists.length > 0) {
                    const allModelBooksInModelPlaylists = modelBooks.every((book) =>
                        model.Playlists.some((playlist) => playlist.PlaylistID === book)
                    );
                    return allModelBooksInModelPlaylists;
                }
            }
        }
        return false;
    }, [packages, modelBooks, modelID]);

    const removeBooksFromPackage = async (packageId) => {
        setIsAdding(true);
        await Axios.post(`/PackagePlaylists/RemoveMulti/${packageId}`, modelBooks);
        forceReloadActivePackages();
        setIsAdding(false);
    };

    const addBooksToPackage = async (packageId, packageGroup = '') => {
        let packageToAdd = packageId;
        if (!packageId) {
            if (singleActivePackage) {
                packageToAdd = singleActivePackage.ID;
            } else {
                return;
            }
        }

        setIsAdding(true);
        try {
            await config.addBooksToPackage(packageToAdd, modelID, modelBooks, packageGroup);
            forceReloadActivePackages();
            setIsAdding(false);
        } catch (error) {
            if (error.errorCode === 'EntityAlreadyExists') {
                setIsAdding(false);
            } else {
                notification.error({
                    message: config.errorMessage,
                    description: error.message,
                });
                setIsAdding(false);
            }
        }
    };

    const sendAsASinglePackage = async () => {
        if (modelBooks.length === 0) {
            return;
        }
        setIsAdding(true);
        const data = {
            Name: getContactName(modelID),
            Models: [
                {
                    Playlists: modelBooks.map((playlist) => ({
                        PlaylistID: playlist,
                        Note: '',
                    })),
                    ModelID: modelID,
                },
            ],
        };
        try {
            const res = await Axios.post(`/packages/create`, data);
            if (res) {
                setIsSingleSend(true);
                setNewPackageId(res.ID);
            }
        } catch (error) {
            console.error('Add Package Error => ', error);
        } finally {
            setIsAdding(false);
        }
    };

    const handleOpenPackage = (selectedPackageId) => {
        setNewPackageId(selectedPackageId);
    };

    const handleClosePackageDrawer = () => {
        setNewPackageId();
        setIsSingleSend(false);
    };

    if (viewModelBooks && modelBooks) {
        // Con un package solo si aggiunge col click. Ma se ha gruppi e la modella non è
        // ancora dentro la destinazione è ambigua: meglio il popover di un default muto.
        const singlePackageDestination = singleActivePackage ? getGroupDestination(singleActivePackage, modelID) : null;
        if (singlePackageDestination && singlePackageDestination.locked) {
            if (booksInSingleActivePackage) {
                return (
                    <NwIcon check disabled pointer icon={config.icon} tooltip={config.alreadyAddedTooltip} size="lg" />
                );
            } else {
                // Su mobile l'hover non esiste e aprirebbe il popover al tap: resta il tooltip.
                if (noTooltip) {
                    return (
                        <NwIcon
                            onClick={() => addBooksToPackage(null, singlePackageDestination.group)}
                            pointer
                            icon={config.icon}
                            tooltip={`Add ${config.booksLabel(modelBooks.length)} to active package`}
                            loading={isAdding}
                            size="lg"
                        />
                    );
                }
                const destination = [singleActivePackage.Name, singlePackageDestination.group]
                    .filter(Boolean)
                    .join(' · ');
                return (
                    <AddModelBooksPreviewPopover
                        variant={variant}
                        modelId={modelID}
                        icon={config.icon}
                        label={`Add ${config.booksLabel(modelBooks.length)}`}
                        destination={`to ${destination}`}
                    >
                        <NwIcon
                            onClick={() => addBooksToPackage(null, singlePackageDestination.group)}
                            pointer
                            icon={config.icon}
                            loading={isAdding}
                            size="lg"
                        />
                    </AddModelBooksPreviewPopover>
                );
            }
        } else {
            return (
                <>
                    <Popover
                        zIndex={9999}
                        overlayClassName="folder-popover"
                        trigger={menuTrigger}
                        mouseEnterDelay={MENU_HOVER_DELAY}
                        mouseLeaveDelay={0.2}
                        open={open}
                        onOpenChange={setOpen}
                        content={
                            <PopoverContent>
                                <div className="popover-header">
                                    <NwIcon icon={config.icon} />
                                    {config.booksLabel(modelBooks.length)}
                                </div>
                                <div className="preview-strip">
                                    {previewLoading || previewBooks.length === 0
                                        ? modelBooks.map((playlistID) => (
                                              <div key={playlistID} className="preview-strip-skeleton" />
                                          ))
                                        : previewBooks.map((book) => (
                                              <ModelBookPreviewCover
                                                  key={book.ID}
                                                  book={book}
                                                  size={PREVIEW_COVER_SIZE}
                                                  withTooltip
                                              />
                                          ))}
                                </div>
                                <Menu
                                    onClick={onClick}
                                    className="nw-small-context-menu"
                                    items={drodownActions.map(renderAction)}
                                />
                            </PopoverContent>
                        }
                    >
                        <NwIcon pointer icon={config.icon} loading={isAdding} size="lg" />
                    </Popover>
                    {createNewPackage && (
                        <AddPackageDrawer
                            onAddedPackage={handleOpenPackage}
                            bookIDs={modelBooks}
                            onClose={() => setCreateNewPackage(false)}
                        />
                    )}
                    {newPackageId && (
                        <PackageDrawer
                            packageId={newPackageId}
                            isSingleSend={isSingleSend}
                            onClose={handleClosePackageDrawer}
                        />
                    )}
                </>
            );
        }
    } else {
        return <NwIcon disabled icon={config.icon} tooltip={config.noBooksTooltip} size="lg" />;
    }
};

export default AddModelBooksToPackageButton;
